<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import apiClient from "@/apiClient.js";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import TimeAgo from "@/components/TimeAgo.vue";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/solid";

const route = useRoute();
const breadcrumbStore = useBreadcrumbStore();
const customerId = route.params.id;
const health = ref({ linked: false });
const loading = ref(true);
const refreshing = ref(false);
const retryingReportId = ref(null);
const message = ref("");

const company = computed(() => health.value?.company ?? {});
const risk = computed(() => health.value?.risk ?? {});
const riskTone = computed(() => ({
  low: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300",
  moderate: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
  high: "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-300",
  critical: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300",
}[risk.value?.band] ?? "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"));

const metricLabels = {
  turnover: "Turnover",
  gross_profit_loss: "Gross profit / loss",
  operating_profit_loss: "Operating profit / loss",
  profit_loss: "Profit / loss",
  fixed_assets: "Fixed assets",
  current_assets: "Current assets",
  stocks: "Stock",
  debtors: "Debtors",
  cash: "Cash",
  creditors_due_within_one_year: "Creditors due within one year",
  net_current_assets_liabilities: "Net current assets / liabilities",
  total_assets_less_current_liabilities: "Total assets less current liabilities",
  creditors_due_after_one_year: "Creditors due after one year",
  net_assets_liabilities: "Net assets / liabilities",
  equity: "Equity",
  average_employees: "Average employees",
};

const accountPeriods = computed(() => {
  const periods = new Map();
  (health.value?.accounts_reports ?? []).forEach((report) => {
    Object.entries(report?.metrics?.periods ?? {}).forEach(([period, metrics]) => {
      if (!periods.has(period)) periods.set(period, metrics);
    });
  });
  return [...periods.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .slice(0, 4)
    .map(([period, metrics]) => ({ period, metrics }));
});

const metricKeys = computed(() => {
  const present = new Set(accountPeriods.value.flatMap(({ metrics }) => Object.keys(metrics ?? {})));
  return Object.keys(metricLabels).filter((key) => present.has(key));
});

const retryableReports = computed(() => (health.value?.accounts_reports ?? []).filter((report) => (
  report.processing_status !== "completed"
  || !Object.keys(report?.metrics?.periods ?? {}).length
)));

const formatDate = (value) => value
  ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(`${String(value).slice(0, 10)}T00:00:00`))
  : "Not available";

const formatMetric = (key, value) => {
  if (value === null || value === undefined) return "—";
  if (key === "average_employees") return Number(value).toLocaleString("en-GB");
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatAddress = (address = {}) => [
  address.premises,
  address.address_line_1,
  address.address_line_2,
  address.locality,
  address.region,
  address.postal_code,
  address.country,
].filter(Boolean).join(", ");

const load = async () => {
  const response = await apiClient.get(`/api/customer/${customerId}/company-health`);
  health.value = response.data ?? { linked: false };
  breadcrumbStore.setValue(company.value.company_name || "Company health");
};

const refresh = async () => {
  refreshing.value = true;
  message.value = "";
  try {
    const response = await apiClient.post(`/api/customer/${customerId}/company-health/refresh`);
    message.value = response.data?.message ?? "Refresh queued.";
    await load();
  } catch (error) {
    message.value = error?.response?.data?.errors?.company_number?.[0]
      ?? error?.response?.data?.message
      ?? "Unable to refresh Companies House data.";
  } finally {
    refreshing.value = false;
  }
};

const retryAccountsReport = async (report) => {
  retryingReportId.value = report.id;
  message.value = "";
  try {
    const response = await apiClient.post(
      `/api/customer/${customerId}/company-health/accounts/${report.id}/retry`,
    );
    health.value = response.data?.health ?? health.value;
    message.value = response.data?.message ?? "Accounts processing retried.";
  } catch (error) {
    message.value = error?.response?.data?.message
      ?? "Unable to retry this accounts document.";
  } finally {
    retryingReportId.value = null;
  }
};

onMounted(async () => {
  try {
    await load();
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="space-y-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <router-link :to="{ name: 'customer-view', params: { id: customerId } }" class="btn inline-flex items-center gap-2">
        <ArrowLeftIcon class="h-4 w-4" /> Customer
      </router-link>
      <button
        v-if="health.linked"
        type="button"
        class="btn accent inline-flex items-center gap-2"
        :disabled="refreshing"
        @click="refresh"
      >
        <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': refreshing }" />
        {{ refreshing ? "Refreshing..." : "Refresh now" }}
      </button>
    </div>

    <p v-if="message" class="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
      {{ message }}
    </p>

    <div v-if="loading" class="box py-12 text-center text-slate-500">Loading company health…</div>
    <div v-else-if="!health.linked" class="box py-12 text-center text-slate-500">
      No Companies House company is linked to this customer.
    </div>

    <template v-else>
      <form-box>
        <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div class="flex items-start gap-4">
            <span class="rounded-2xl bg-cyan-100 p-3 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">
              <BuildingOffice2Icon class="h-8 w-8" />
            </span>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Companies House</p>
              <h1 class="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{{ company.company_name }}</h1>
              <div class="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <a :href="company.companies_house_url" target="_blank" rel="noopener noreferrer" class="text-cyan-700 hover:underline dark:text-cyan-300">
                  {{ company.company_number }}
                </a>
                <span class="rounded-full bg-slate-100 px-2.5 py-1 capitalize dark:bg-slate-700">{{ company.company_status }}</span>
                <span>{{ company.company_type }}</span>
              </div>
              <p class="mt-3 max-w-3xl text-sm text-slate-500 dark:text-slate-400">{{ formatAddress(company.registered_office_address) }}</p>
            </div>
          </div>

          <div class="rounded-2xl border p-4 text-right" :class="riskTone">
            <p class="text-xs font-semibold uppercase tracking-wide">Risk score</p>
            <p class="mt-1 text-4xl font-bold">{{ risk.score ?? "—" }}<span class="text-base font-normal"> / 100</span></p>
            <p class="mt-1 text-sm capitalize">{{ risk.band || "Pending" }} · {{ risk.confidence || "No" }} confidence</p>
            <p v-if="risk.assessed_at" class="mt-2 text-xs opacity-75">Assessed <TimeAgo :data="risk.assessed_at" /></p>
          </div>
        </div>
      </form-box>

      <div class="grid gap-4 xl:grid-cols-3">
        <form-box header="Risk analysis" class="xl:col-span-2">
          <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
            {{ risk.summary || "The initial assessment will appear after the first monitoring check." }}
          </p>
          <div v-if="risk.factors?.length" class="mt-4 space-y-2">
            <div v-for="factor in risk.factors" :key="`${factor.code}-${factor.label}`" class="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              <div class="flex items-center justify-between gap-3">
                <p class="font-semibold text-slate-800 dark:text-slate-100">{{ factor.label }}</p>
                <span v-if="factor.points" class="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">+{{ factor.points }}</span>
              </div>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">{{ factor.evidence }}</p>
            </div>
          </div>
        </form-box>

        <form-box header="Monitoring">
          <dl class="space-y-4 text-sm">
            <div>
              <dt class="text-slate-500">Last successful check</dt>
              <dd class="mt-1 font-medium text-slate-800 dark:text-slate-100"><TimeAgo v-if="company.last_successful_at" :data="company.last_successful_at" /><span v-else>Pending</span></dd>
            </div>
            <div>
              <dt class="text-slate-500">Next scheduled check</dt>
              <dd class="mt-1 font-medium text-slate-800 dark:text-slate-100">{{ formatDate(company.next_check_at) }}</dd>
            </div>
            <div v-if="company.last_error" class="rounded-xl bg-rose-50 p-3 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
              {{ company.last_error }}
            </div>
          </dl>
        </form-box>
      </div>

      <form-box header="Statutory filing health">
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div class="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <p class="text-xs uppercase tracking-wide text-slate-500">Accounts due</p>
            <p class="mt-2 font-semibold">{{ formatDate(company.accounts?.next_accounts?.due_on) }}</p>
            <p class="mt-2 inline-flex items-center gap-1 text-xs" :class="company.accounts?.next_accounts?.overdue ? 'text-rose-600' : 'text-emerald-600'">
              <ExclamationTriangleIcon v-if="company.accounts?.next_accounts?.overdue" class="h-4 w-4" />
              <CheckCircleIcon v-else class="h-4 w-4" />
              {{ company.accounts?.next_accounts?.overdue ? "Overdue" : "Not overdue" }}
            </p>
          </div>
          <div class="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <p class="text-xs uppercase tracking-wide text-slate-500">Latest accounts</p>
            <p class="mt-2 font-semibold">{{ formatDate(company.accounts?.last_accounts?.period_end_on) }}</p>
            <p class="mt-2 text-xs capitalize text-slate-500">{{ company.accounts?.last_accounts?.type || "Type unavailable" }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <p class="text-xs uppercase tracking-wide text-slate-500">Confirmation statement due</p>
            <p class="mt-2 font-semibold">{{ formatDate(company.confirmation_statement?.next_due) }}</p>
            <p class="mt-2 inline-flex items-center gap-1 text-xs" :class="company.confirmation_statement?.overdue ? 'text-rose-600' : 'text-emerald-600'">
              <ExclamationTriangleIcon v-if="company.confirmation_statement?.overdue" class="h-4 w-4" />
              <CheckCircleIcon v-else class="h-4 w-4" />
              {{ company.confirmation_statement?.overdue ? "Overdue" : "Not overdue" }}
            </p>
          </div>
          <div class="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <p class="text-xs uppercase tracking-wide text-slate-500">Incorporated</p>
            <p class="mt-2 font-semibold">{{ formatDate(company.date_of_creation) }}</p>
            <p class="mt-2 text-xs capitalize text-slate-500">{{ company.jurisdiction }}</p>
          </div>
        </div>
      </form-box>

      <form-box header="Reported financials">
        <div v-if="accountPeriods.length" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
            <thead><tr><th class="py-3 pr-4 text-left text-xs uppercase text-slate-500">Metric</th><th v-for="item in accountPeriods" :key="item.period" class="px-4 py-3 text-right text-xs uppercase text-slate-500">{{ formatDate(item.period) }}</th></tr></thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-for="key in metricKeys" :key="key">
                <td class="py-3 pr-4 font-medium text-slate-700 dark:text-slate-200">{{ metricLabels[key] }}</td>
                <td v-for="item in accountPeriods" :key="`${key}-${item.period}`" class="px-4 py-3 text-right tabular-nums">{{ formatMetric(key, item.metrics?.[key]) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="flex items-start gap-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-400">
          <DocumentTextIcon class="h-5 w-5 shrink-0" />
          <p>No structured balance-sheet figures are available yet. Filed documents are still retained for review when Companies House provides only PDF or limited accounts.</p>
        </div>

        <div v-if="retryableReports.length" class="mt-4 space-y-3">
          <div
            v-for="report in retryableReports"
            :key="report.id"
            class="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30"
          >
            <div class="min-w-0">
              <p class="font-medium text-amber-900 dark:text-amber-200">
                {{ report.filing?.description || "Accounts document" }}
              </p>
              <p class="mt-1 text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400">
                {{ report.processing_status?.replaceAll("_", " ") || "Processing incomplete" }}
                <span v-if="report.filing?.filed_on"> · filed {{ formatDate(report.filing.filed_on) }}</span>
              </p>
              <p v-if="report.processing_error" class="mt-2 text-sm text-amber-800 dark:text-amber-300">
                {{ report.processing_error }}
              </p>
            </div>
            <button
              type="button"
              class="btn inline-flex shrink-0 items-center gap-2"
              :disabled="retryingReportId !== null"
              @click="retryAccountsReport(report)"
            >
              <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': retryingReportId === report.id }" />
              {{ retryingReportId === report.id ? "Retrying..." : "Retry" }}
            </button>
          </div>
        </div>
      </form-box>

      <div class="grid gap-4 xl:grid-cols-2">
        <form-box header="Changes since monitoring began">
          <div v-if="health.events?.length" class="space-y-4">
            <div v-for="event in health.events" :key="event.id" class="flex gap-3">
              <span class="mt-0.5 rounded-full p-1.5" :class="event.severity === 'critical' ? 'bg-rose-100 text-rose-600' : event.severity === 'warning' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'">
                <ExclamationTriangleIcon v-if="event.severity !== 'information'" class="h-4 w-4" />
                <ClockIcon v-else class="h-4 w-4" />
              </span>
              <div>
                <p class="font-medium text-slate-800 dark:text-slate-100">{{ event.title }}</p>
                <p v-if="event.description" class="mt-1 text-sm text-slate-500">{{ event.description }}</p>
                <p class="mt-1 text-xs text-slate-400">Observed <TimeAgo :data="event.observed_at" /></p>
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-slate-500">No changes have been detected since monitoring began.</p>
        </form-box>

        <form-box header="Recent filings">
          <div class="max-h-[480px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
            <div v-for="filing in health.filings" :key="filing.id" class="py-3 first:pt-0">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="font-medium text-slate-800 dark:text-slate-100">{{ filing.description || filing.type }}</p>
                  <p class="mt-1 text-xs uppercase tracking-wide text-slate-400">{{ filing.category }} · {{ filing.type }}</p>
                </div>
                <span class="whitespace-nowrap text-xs text-slate-500">{{ formatDate(filing.filed_on) }}</span>
              </div>
            </div>
          </div>
        </form-box>
      </div>
    </template>
  </div>
</template>
